import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  User,
  Phone,
  CreditCard,
  DollarSign,
  Calendar,
  UserCheck,
  Edit,
  Trash2,
  Plus,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Banknote,
  CalendarDays,
  Receipt,
  TrendingUp,
  TrendingDown,
  Minus,
  FileText,
  Download
} from 'lucide-react';
import { formatDate } from '../../utils/date';

const PaymentDetails = ({ isOpen, onClose, traveler, campaignName, onEdit, onDelete, onAddPayment }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !traveler) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'pending':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'overdue':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return CheckCircle;
      case 'partial':
        return Clock;
      case 'pending':
        return Calendar;
      case 'overdue':
        return AlertCircle;
      default:
        return XCircle;
    }
  };

  const calculateProgress = () => {
    if (traveler.paymentType === 'cash') {
      return traveler.paymentStatus === 'paid' ? 100 : 0;
    } else {
      const totalAmount = traveler.totalAmount;
      const paidAmount = totalAmount - (traveler.remainingBalance || 0);
      return totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;
    }
  };

  const formatCurrency = (amount) => {
    return `${(amount || 0).toLocaleString()} ${t('common:currency')}`;
  };

  // Mock payment history data
  const paymentHistory = [
    {
      id: 1,
      date: '2024-01-15',
      type: 'payment',
      amount: traveler.paymentType === 'installments' ? (traveler.downPayment || 0) : traveler.totalAmount,
      description: traveler.paymentType === 'installments' ? t('payments.downPayment') : t('payments.fullPayment'),
      method: 'cash',
      status: 'completed'
    },
    ...(traveler.paymentType === 'installments' ? [
      {
        id: 2,
        date: '2024-02-15',
        type: 'payment',
        amount: traveler.monthlyInstallment || 0,
        description: `${t('payments.installment')} #1`,
        method: 'bank_transfer',
        status: 'completed'
      },
      {
        id: 3,
        date: '2024-03-15',
        type: 'payment',
        amount: traveler.monthlyInstallment || 0,
        description: `${t('payments.installment')} #2`,
        method: 'cash',
        status: 'pending'
      }
    ] : [])
  ];

  const StatusIcon = getStatusIcon(traveler.paymentStatus);
  const progress = calculateProgress();
  const paidAmount = traveler.totalAmount - (traveler.remainingBalance || 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {traveler.fullName}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {campaignName} • {t('payments.paymentDetails')}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(traveler.paymentStatus)}`}>
              <StatusIcon className="h-4 w-4 mr-1" />
              {t(`payments.${traveler.paymentStatus}`)}
            </span>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('payments.overview')}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('payments.paymentHistory')}
            </button>
            <button
              onClick={() => setActiveTab('schedule')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'schedule'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('payments.paymentSchedule')}
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 dark:text-blue-400">
                        {t('payments.totalAmount')}
                      </p>
                      <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                        {formatCurrency(traveler.totalAmount)}
                      </p>
                    </div>
                    <DollarSign className="h-8 w-8 text-blue-500" />
                  </div>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 dark:text-green-400">
                        {t('payments.paidAmount')}
                      </p>
                      <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                        {formatCurrency(paidAmount)}
                      </p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-green-500" />
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-yellow-600 dark:text-yellow-400">
                        {t('payments.remainingBalance')}
                      </p>
                      <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
                        {formatCurrency(traveler.remainingBalance || 0)}
                      </p>
                    </div>
                    <Clock className="h-8 w-8 text-yellow-500" />
                  </div>
                </div>

                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600 dark:text-purple-400">
                        {t('payments.progress')}
                      </p>
                      <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                        {progress.toFixed(1)}%
                      </p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-purple-500" />
                  </div>
                </div>
              </div>

              {/* Payment Progress */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {t('payments.paymentProgress')}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {t('payments.completionRate')}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {progress.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-300 ${
                        progress === 100 
                          ? 'bg-green-500' 
                          : progress > 50 
                            ? 'bg-blue-500' 
                            : 'bg-yellow-500'
                      }`}
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>{formatCurrency(0)}</span>
                    <span>{formatCurrency(paidAmount)}</span>
                    <span>{formatCurrency(traveler.totalAmount)}</span>
                  </div>
                </div>
              </div>

              {/* Traveler Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    {t('payments.travelerInfo')}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center text-sm">
                      <Phone className="h-4 w-4 mr-3 text-gray-400" />
                      <span className="text-gray-600 dark:text-gray-400 w-20">
                        {t('travelers.phone')}
                      </span>
                      <span className="text-gray-900 dark:text-white">
                        {traveler.primaryPhone}
                      </span>
                    </div>
                    <div className="flex items-center text-sm">
                      <CreditCard className="h-4 w-4 mr-3 text-gray-400" />
                      <span className="text-gray-600 dark:text-gray-400 w-20">
                        {t('travelers.passport')}
                      </span>
                      <span className="text-gray-900 dark:text-white">
                        {traveler.passportNumber}
                      </span>
                    </div>
                    {traveler.agentName && (
                      <div className="flex items-center text-sm">
                        <UserCheck className="h-4 w-4 mr-3 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-400 w-20">
                          {t('travelers.agent')}
                        </span>
                        <span className="text-gray-900 dark:text-white">
                          {traveler.agentName}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    {t('payments.paymentInfo')}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        {t('payments.paymentType')}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        traveler.paymentType === 'cash'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                      }`}>
                        {traveler.paymentType === 'cash' ? (
                          <Banknote className="h-3 w-3 mr-1" />
                        ) : (
                          <CalendarDays className="h-3 w-3 mr-1" />
                        )}
                        {t(`payments.${traveler.paymentType}`)}
                      </span>
                    </div>
                    {traveler.paymentType === 'installments' && (
                      <>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">
                            {t('payments.monthlyInstallment')}
                          </span>
                          <span className="text-gray-900 dark:text-white font-medium">
                            {formatCurrency(traveler.monthlyInstallment)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">
                            {t('payments.nextPaymentDue')}
                          </span>
                          <span className="text-gray-900 dark:text-white font-medium">
                            {formatDate(traveler.nextPaymentDate)}
                          </span>
                        </div>
                      </>
                    )}
                    {traveler.agentCommission && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">
                          {t('payments.agentCommission')}
                        </span>
                        <span className="text-purple-600 dark:text-purple-400 font-medium">
                          {formatCurrency(traveler.agentCommission)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {t('payments.paymentHistory')}
                </h3>
                <button
                  onClick={onAddPayment}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('payments.addPayment')}
                </button>
              </div>

              <div className="space-y-3">
                {paymentHistory.map((payment) => (
                  <div key={payment.id} className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          payment.status === 'completed'
                            ? 'bg-green-100 dark:bg-green-900'
                            : 'bg-yellow-100 dark:bg-yellow-900'
                        }`}>
                          {payment.type === 'payment' ? (
                            <TrendingUp className={`h-5 w-5 ${
                              payment.status === 'completed'
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-yellow-600 dark:text-yellow-400'
                            }`} />
                          ) : (
                            <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {payment.description}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(payment.date)} • {t(`payments.${payment.method}`)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${
                          payment.type === 'payment'
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {payment.type === 'payment' ? '+' : '-'}{formatCurrency(payment.amount)}
                        </p>
                        <p className={`text-sm ${
                          payment.status === 'completed'
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-yellow-600 dark:text-yellow-400'
                        }`}>
                          {t(`payments.${payment.status}`)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'schedule' && traveler.paymentType === 'installments' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {t('payments.paymentSchedule')}
              </h3>

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                  {t('payments.scheduleOverview')}
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-blue-600 dark:text-blue-400">
                      {t('payments.totalInstallments')}
                    </span>
                    <p className="font-medium text-blue-900 dark:text-blue-100">
                      {traveler.paymentDuration || 0}
                    </p>
                  </div>
                  <div>
                    <span className="text-blue-600 dark:text-blue-400">
                      {t('payments.monthlyAmount')}
                    </span>
                    <p className="font-medium text-blue-900 dark:text-blue-100">
                      {formatCurrency(traveler.monthlyInstallment)}
                    </p>
                  </div>
                  <div>
                    <span className="text-blue-600 dark:text-blue-400">
                      {t('payments.completed')}
                    </span>
                    <p className="font-medium text-blue-900 dark:text-blue-100">
                      2 / {traveler.paymentDuration || 0}
                    </p>
                  </div>
                  <div>
                    <span className="text-blue-600 dark:text-blue-400">
                      {t('payments.nextDue')}
                    </span>
                    <p className="font-medium text-blue-900 dark:text-blue-100">
                      {formatDate(traveler.nextPaymentDate)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Schedule Timeline */}
              <div className="space-y-3">
                {Array.from({ length: traveler.paymentDuration || 0 }, (_, index) => {
                  const installmentDate = new Date();
                  installmentDate.setMonth(installmentDate.getMonth() + index + 1);
                  const isPaid = index < 2; // Mock: first 2 installments are paid
                  const isCurrent = index === 2; // Mock: 3rd installment is current
                  
                  return (
                    <div key={index} className={`flex items-center p-4 rounded-lg border ${
                      isPaid
                        ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700'
                        : isCurrent
                          ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-700'
                          : 'bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600'
                    }`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 ${
                        isPaid
                          ? 'bg-green-500 text-white'
                          : isCurrent
                            ? 'bg-yellow-500 text-white'
                            : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                      }`}>
                        {isPaid ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : isCurrent ? (
                          <Clock className="h-4 w-4" />
                        ) : (
                          <Minus className="h-4 w-4" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`font-medium ${
                              isPaid
                                ? 'text-green-900 dark:text-green-100'
                                : isCurrent
                                  ? 'text-yellow-900 dark:text-yellow-100'
                                  : 'text-gray-900 dark:text-white'
                            }`}>
                              {t('payments.installment')} #{index + 1}
                            </p>
                            <p className={`text-sm ${
                              isPaid
                                ? 'text-green-600 dark:text-green-400'
                                : isCurrent
                                  ? 'text-yellow-600 dark:text-yellow-400'
                                  : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              {formatDate(installmentDate)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={`font-semibold ${
                              isPaid
                                ? 'text-green-900 dark:text-green-100'
                                : isCurrent
                                  ? 'text-yellow-900 dark:text-yellow-100'
                                  : 'text-gray-900 dark:text-white'
                            }`}>
                              {formatCurrency(traveler.monthlyInstallment)}
                            </p>
                            <p className={`text-sm ${
                              isPaid
                                ? 'text-green-600 dark:text-green-400'
                                : isCurrent
                                  ? 'text-yellow-600 dark:text-yellow-400'
                                  : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              {isPaid ? t('payments.paid') : isCurrent ? t('payments.due') : t('payments.upcoming')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'schedule' && traveler.paymentType === 'cash' && (
            <div className="text-center py-12">
              <Receipt className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('payments.noScheduleForCash')}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {t('payments.cashPaymentNoSchedule')}
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <button className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors">
              <Download className="h-4 w-4 mr-2" />
              {t('payments.downloadReceipt')}
            </button>
            <button className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors">
              <FileText className="h-4 w-4 mr-2" />
              {t('payments.generateReport')}
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={onEdit}
              className="flex items-center px-4 py-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
            >
              <Edit className="h-4 w-4 mr-2" />
              {t('common.edit')}
            </button>
            <button
              onClick={onDelete}
              className="flex items-center px-4 py-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t('common.delete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetails;